import React, { useEffect, useState } from "react";
import PageLayout from "../components/PageLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ChartLineUp, Users, GearSix, Storefront, Percent } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function AdminHome() {
  const [summary, setSummary] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [opts, setOpts] = useState([]);
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState(null);
  const [newOpt, setNewOpt] = useState({ label: "", default_fee: "" });

  const load = () => {
    api.get("/admin/summary").then(({ data }) => setSummary(data)).catch(() => toast.error("Admin only"));
    api.get("/admin/fee-config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/complexity-options").then(({ data }) => setOpts(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/config").then(({ data }) => setConfig(data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    try {
      await api.put("/admin/fee-config", cfg);
      toast.success("Fee config saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const addOpt = async () => {
    if (!newOpt.label) return;
    try {
      await api.post("/admin/complexity-options", {
        option_id: "",
        label: newOpt.label,
        default_fee: Number(newOpt.default_fee) || 0,
        active: true,
      });
      setNewOpt({ label: "", default_fee: "" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const toggleOpt = async (o) => {
    try {
      await api.put(`/admin/complexity-options/${o.option_id}`, { ...o, active: !o.active });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <PageLayout>
      <div className="max-w-6xl">
        <div className="kicker">Admin</div>
        <h1 className="display-serif text-4xl sm:text-5xl mt-2">Platform.</h1>

        {config && !config.paypal_configured && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="admin-paypal-warning">
            <b>PayPal not configured.</b> Add <code>PAYPAL_CLIENT_ID</code> and <code>PAYPAL_CLIENT_SECRET</code> to <code>backend/.env</code> and restart the backend. Currently running in <b>{config.paypal_mode}</b> mode.
          </div>
        )}

        {/* Metrics */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric Icon={Percent} kicker="Your 6%" title={`$${(summary?.platform_earned ?? 0).toFixed(2)}`} sub="Total earned" />
          <Metric Icon={ChartLineUp} kicker="Escrow" title={`$${(summary?.pending_escrow ?? 0).toFixed(2)}`} sub="Held for jobs in progress" />
          <Metric Icon={Storefront} kicker="Jobs" title={summary?.total_jobs ?? 0} sub={`${summary?.paid_jobs ?? 0} paid`} />
          <Metric Icon={Users} kicker="Users" title={summary?.total_users ?? 0} sub={`${summary?.total_appraisers ?? 0} appraisers`} />
        </div>

        <Tabs defaultValue="fees" className="mt-10">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="fees" data-testid="admin-tab-fees">Fee formula</TabsTrigger>
            <TabsTrigger value="complexity" data-testid="admin-tab-complexity">Complexity</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="fees" className="mt-6">
            {cfg && (
              <div className="surface-card p-6 lg:p-8">
                <h3 className="display-serif text-xl">AI minimum fee formula</h3>
                <p className="text-sm text-zinc-600 mt-1">Tune the base formula. AI uses this as its starting point.</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(cfg).map(([k, v]) => (
                    <div key={k}>
                      <Label>{k}</Label>
                      <Input type="number" value={v} step="0.01" onChange={e => setCfg({ ...cfg, [k]: Number(e.target.value) })} data-testid={`cfg-${k}`} />
                    </div>
                  ))}
                </div>
                <button className="btn-ink mt-6" onClick={saveCfg} data-testid="cfg-save-btn">Save</button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="complexity" className="mt-6">
            <div className="surface-card p-6 lg:p-8">
              <h3 className="display-serif text-xl">Complexity add-on options</h3>
              <div className="mt-4 grid sm:grid-cols-[1fr,140px,120px] gap-3 items-end">
                <div>
                  <Label>New option label</Label>
                  <Input value={newOpt.label} onChange={e => setNewOpt({ ...newOpt, label: e.target.value })} data-testid="opt-new-label" />
                </div>
                <div>
                  <Label>Default $</Label>
                  <Input type="number" value={newOpt.default_fee} onChange={e => setNewOpt({ ...newOpt, default_fee: e.target.value })} data-testid="opt-new-fee" />
                </div>
                <button className="btn-outline-ink" onClick={addOpt} data-testid="opt-add-btn">Add</button>
              </div>
              <div className="mt-6 divide-y divide-zinc-100">
                {opts.map(o => (
                  <div key={o.option_id} className="py-3 flex items-center gap-4">
                    <div className="flex-1">
                      <div className="font-medium">{o.label}</div>
                      <div className="text-xs text-zinc-500">${o.default_fee}</div>
                    </div>
                    <span className={`chip ${o.active ? "bg-emerald-100 text-emerald-900" : "bg-zinc-100 text-zinc-500"}`}>{o.active ? "Active" : "Off"}</span>
                    <button onClick={() => toggleOpt(o)} className="text-sm text-[#115E59] hover:underline" data-testid={`opt-toggle-${o.option_id}`}>
                      {o.active ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50/70 text-zinc-500">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium">Name</th>
                    <th className="text-left px-6 py-3 font-medium">Email</th>
                    <th className="text-left px-6 py-3 font-medium">Roles</th>
                    <th className="text-left px-6 py-3 font-medium">Reviewer</th>
                    <th className="text-left px-6 py-3 font-medium">PayPal</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.user_id} className="border-t border-zinc-100">
                      <td className="px-6 py-3">{u.name}</td>
                      <td className="px-6 py-3 text-zinc-600">{u.email}</td>
                      <td className="px-6 py-3">
                        {u.roles?.map(r => <span key={r} className="chip bg-[#CCFBF1] text-[#115E59] mr-1">{r}</span>)}
                      </td>
                      <td className="px-6 py-3">{u.is_reviewer ? "Yes" : "—"}</td>
                      <td className="px-6 py-3 text-zinc-600 text-xs">{u.paypal_email || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
}

function Metric({ Icon, kicker, title, sub }) {
  return (
    <div className="surface-card p-6">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#CCFBF1] text-[#115E59]">
        <Icon size={18} weight="duotone" />
      </span>
      <div className="kicker mt-4">{kicker}</div>
      <div className="display-serif text-3xl mt-1">{title}</div>
      <div className="text-sm text-zinc-600">{sub}</div>
    </div>
  );
}
